import React from 'react';
import { useHistory, useRouteMatch } from 'react-router';
import Tooltip from '../../../../components/Tooltip';

import Icon from 'components/Input/Icon';
import {
  KpiHeading,
  KpiTitle,
  KpiTitleLink,
  Result,
  IconsWrapper,
  ExclamationMarkIcon,
  ValueText,
  TableContainer,
  HeaderRow,
  DataRow,
  Cell,
  HeaderCell,
  IconCell,
  TableEl,
  IconHeaderCell,
} from './style';
import utils from 'utils';
import { URLS } from 'utils/constants';
import { KpiElement } from 'routes/ControlTower/interfaces';
import { SEMAPHORE_COLORS } from '.';
import { formatKpiValue } from 'routes/ControlTower/utils';
import styled from 'styled-components/macro';

const Container = styled.div`
  position: relative;
`;

const HeadingContainer = styled.div`
  display: flex;
  justify-content: space-between;
  padding-bottom: 10px;
`;

export const KpiElementTable = ({ kpi, categoryId, exportReport, viewReport, drillDownProtocols }: KpiElement) => {
  const history = useHistory();
  const t = utils.intl.useTranslator();

  // @note we need to understand how to retreive this param
  const idGeoList = null;

  const { path } = useRouteMatch();

  const { name, tooltip, idCharts, idReport, childMetrics, semaphore, idMetric, idProgram, idTemplate, customIcon } =
    kpi;

  const openChartPage = (idCharts: number | number[]) => {
    history.push({
      pathname: `${path}/${URLS.controlTower.chart}`,
      state: {
        idCharts,
        categoryId,
      },
    });
  };

  const showTooltipCol = (childMetrics || []).some(({ tooltip }) => !!tooltip);
  const showExportCol = (childMetrics || []).some(({ idReport }) => !!idReport);
  const showExportViewCol = showExportCol;
  const showLinkCol = (childMetrics || []).some(
    ({ idMetric, idProgram, idTemplate }) => !!(idMetric && idProgram && idTemplate),
  );
  const showIconCol = (childMetrics || []).some(({ customIcon }) => !!customIcon);

  if (!childMetrics || childMetrics.length < 0) return null;

  return (
    <Container>
      <HeadingContainer>
        <KpiHeading>
          {idCharts && !(Array.isArray(idCharts) && idCharts.length === 0) ? (
            <KpiTitle>
              <KpiTitleLink onClick={() => openChartPage(idCharts)}>{name}:</KpiTitleLink>{' '}
              <Result color={SEMAPHORE_COLORS[semaphore as keyof typeof SEMAPHORE_COLORS]}>
                {formatKpiValue(t, kpi)}
              </Result>
            </KpiTitle>
          ) : (
            <KpiTitle>
              {name}:{' '}
              <Result color={SEMAPHORE_COLORS[semaphore as keyof typeof SEMAPHORE_COLORS]}>
                {formatKpiValue(t, kpi)}
              </Result>
            </KpiTitle>
          )}
          {semaphore === 'RED' && <ExclamationMarkIcon />}

          <IconsWrapper>
            {customIcon && <Icon icon={customIcon} size="l" />}
            {tooltip && (
              <Tooltip text={tooltip}>
                <Icon size="l" icon="info" />
              </Tooltip>
            )}
            {idReport && (
              <Tooltip text={utils.app.textTranslated('Export report', t)}>
                <Icon size="l" icon="arrow-down-circle" onClick={() => exportReport(idReport)} />
              </Tooltip>
            )}
            {idReport && (
              <Tooltip text={utils.app.textTranslated('Open report', t)}>
                <Icon size="m" icon="square-link" onClick={() => viewReport(idReport)} />
              </Tooltip>
            )}
            {idProgram && idTemplate && idMetric && (
              <Tooltip text={utils.app.textTranslated('Link to documents', t)}>
                <Icon size="l" icon="arrow-up-circle" onClick={() => drillDownProtocols(idMetric, idGeoList)} />
              </Tooltip>
            )}
          </IconsWrapper>
        </KpiHeading>
      </HeadingContainer>
      <TableContainer>
        <TableEl>
          <HeaderRow>
            {showIconCol && <IconHeaderCell />}

            <HeaderCell>{utils.app.textTranslated('KPI-table-header', t, 'KPI')}</HeaderCell>
            <HeaderCell>{utils.app.textTranslated('Value-table-header', t, 'Value')}</HeaderCell>
            {showTooltipCol && <IconHeaderCell />}
            {showExportCol && <IconHeaderCell />}
            {showExportViewCol && <IconHeaderCell />}
            {showLinkCol && <IconHeaderCell />}
          </HeaderRow>

          {childMetrics.map((metric, rIdx) => {
            const { tooltip, idReport, idTemplate, idProgram, idCharts, name, customIcon } = metric;
            const hasCharts = idCharts && idCharts?.length > 0;
            const hasLink = idMetric && idProgram && idTemplate;
            return (
              <DataRow key={rIdx}>
                {showIconCol && <IconCell>{customIcon && <Icon icon={customIcon} size="xl" />}</IconCell>}

                <Cell>
                  {hasCharts ? (
                    <KpiTitleLink onClick={() => openChartPage(idCharts || [])}>{name}</KpiTitleLink>
                  ) : (
                    <KpiTitle>{name}</KpiTitle>
                  )}
                </Cell>
                <Cell>
                  <ValueText>{formatKpiValue(t, metric)}</ValueText>
                </Cell>
                {showTooltipCol && (
                  <IconCell>
                    {tooltip ? (
                      <Tooltip text={tooltip}>
                        <Icon size="xl" icon="info" />
                      </Tooltip>
                    ) : null}
                  </IconCell>
                )}
                {showExportCol && (
                  <IconCell>
                    {idReport ? (
                      <Tooltip text={utils.app.textTranslated('Export report', t)}>
                        <Icon size="xl" icon="arrow-down-circle" onClick={() => exportReport(idReport as number)} />
                      </Tooltip>
                    ) : null}
                  </IconCell>
                )}
                {showExportViewCol && (
                  <IconCell>
                    {idReport ? (
                      <Tooltip text={utils.app.textTranslated('Open report', t)}>
                        <Icon size="lg" icon="square-link" onClick={() => viewReport(idReport)} />
                      </Tooltip>
                    ) : null}
                  </IconCell>
                )}
                {showLinkCol && (
                  <IconCell>
                    {hasLink ? (
                      <Tooltip text={utils.app.textTranslated('Link to documents', t)}>
                        <Icon
                          size="xl"
                          icon="arrow-up-circle"
                          onClick={() => drillDownProtocols(idMetric as number, idGeoList)}
                        />
                      </Tooltip>
                    ) : null}
                  </IconCell>
                )}
              </DataRow>
            );
          })}
        </TableEl>
      </TableContainer>
    </Container>
  );
};
